import React, { useCallback, useEffect, useState } from 'react';
import api, { errMsg } from '../services/api';
import { fmt } from '../services/format';
import Msg from '../components/Msg';
import Pager from '../components/Pager';
import Modal from '../components/Modal';

const SIZE = 25;

function FamilyDetail({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [max, setMax] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/families/${id}`);
      setD(r.data);
      setMax(String(r.data.family.maxMembers));
    } catch (e) {
      setMsg({ type: 'err', text: errMsg(e) });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okText, closeAfter) => {
    setBusy(true); setMsg(null);
    try {
      await fn();
      onChanged();
      if (closeAfter) { onClose(); return; }
      setMsg({ type: 'ok', text: okText });
      await load();
    } catch (e) {
      setMsg({ type: 'err', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const f = d?.family;
  return (
    <Modal title={f ? f.name : 'Familie'} onClose={onClose}>
      <Msg msg={msg} />
      {!d && !msg && <p className="muted">Se încarcă…</p>}
      {d && (
        <>
          <p className="muted small">
            Creată {fmt(f.createdAt)} · {f.memberCount}/{f.maxMembers} membri ·{' '}
            <span className={`badge badge-${f.status}`}>{f.status === 'activ' ? 'activă' : 'suspendată'}</span>
          </p>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Nume</th><th>Email</th><th>Rol</th><th>Status</th><th>Ultima conectare</th></tr></thead>
              <tbody>
                {d.members.map(m => (
                  <tr key={m.memberId}>
                    <td>{m.displayName}</td><td>{m.email}</td><td>{m.role}</td>
                    <td>{m.status === 'invitat' ? 'invitat' : 'activ'}</td><td>{fmt(m.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="subsection">
            <h4>Limita de membri</h4>
            <div className="row-form">
              <input type="number" min={2} max={50} value={max} onChange={e => setMax(e.target.value)} />
              <button className="btn" disabled={busy || Number(max) === f.maxMembers}
                onClick={() => act(() => api.put(`/api/families/${id}/max-members`, { maxMembers: Number(max) }), 'Limita a fost schimbată.')}>
                Salvează limita
              </button>
            </div>
          </div>

          <div className="btn-row">
            <button className="btn" disabled={busy}
              onClick={() => act(() => api.put(`/api/families/${id}/status`, { status: f.status === 'activ' ? 'suspendat' : 'activ' }),
                f.status === 'activ' ? 'Familia a fost suspendată.' : 'Familia a fost reactivată.')}>
              {f.status === 'activ' ? 'Suspendă familia' : 'Reactivează familia'}
            </button>
            <button className="btn btn-danger" disabled={busy}
              onClick={() => window.confirm(`Ștergi definitiv familia „${f.name}” și apartenențele ei? Conturile membrilor rămân.`)
                && act(() => api.delete(`/api/families/${id}`), '', true)}>
              Șterge familia
            </button>
          </div>
          <p className="muted small">O familie suspendată nu mai poate folosi harta; membrii își văd doar lista de familii.</p>
        </>
      )}
    </Modal>
  );
}

export default function Families() {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/families', { params: { q: query, page, size: SIZE } });
      setData(r.data);
      setMsg(null);
    } catch (e) {
      setMsg({ type: 'err', text: errMsg(e) });
    }
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h2>Clienți (familii)</h2>
      <form className="toolbar" onSubmit={e => { e.preventDefault(); setPage(0); setQuery(q); }}>
        <input placeholder="Caută după numele familiei, nume sau email de membru" value={q} onChange={e => setQ(e.target.value)} />
        <button className="btn">Caută</button>
      </form>
      <Msg msg={msg} />
      {!data && !msg && <p className="muted">Se încarcă…</p>}
      {data && (
        <>
          <div className="table-wrap">
            <table className="clickable">
              <thead><tr><th>Familie</th><th>Membri</th><th>Admini</th><th>Status</th><th>Creată</th></tr></thead>
              <tbody>
                {data.items.map(f => (
                  <tr key={f.familyId} onClick={() => setOpenId(f.familyId)}>
                    <td><b>{f.name}</b></td>
                    <td>{f.memberCount}/{f.maxMembers}</td>
                    <td className="small">{f.admins || '—'}</td>
                    <td><span className={`badge badge-${f.status}`}>{f.status === 'activ' ? 'activă' : 'suspendată'}</span></td>
                    <td>{fmt(f.createdAt)}</td>
                  </tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan={5} className="muted">Nicio familie.</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} size={data.size} total={data.total} onPage={setPage} />
        </>
      )}
      {openId != null && <FamilyDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
